import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  BonusApi,
  Bus,
  NotificationManager,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';

import { bonusStatuses, bonusTypes, FREE_SPIN_LOAD_STATUSES } from 'src/common/constants';
import useBonusesService from 'src/hooks/useBonusesService';

import {
  escapeRegExp,
  findPreselectBonus,
  getBonusNotAllowedCountries,
  getBonusNotAllowedPaymentMethods,
  getMinDeposit,
  isAmountBelowMinDeposit,
  isCasinoBonus,
  isDepositBonus,
  isDepositFreeSpin,
  markBonusAvailability,
  normalizePath,
  sortBonusesByAvailabilityAndAmount,
} from './utils';

const { DEPOSIT, RELOAD_DEPOSIT } = bonusTypes;
const CASINO_PRODUCT_TYPE = 'CASINO';
const { ACTIVATION_READY } = bonusStatuses;

const PROVIDER_MODE = {
  DEPOSIT: 'deposit',
  QUICK: 'quick',
};
const EMPTY_BONUSES = [];
const findBonusById = (bonusesList, id) =>
  bonusesList.find(bonus => (bonus.bonusInstanceId ?? bonus.id) === id);
const createProviderModeState = defaultValue => ({
  [PROVIDER_MODE.DEPOSIT]: defaultValue,
  [PROVIDER_MODE.QUICK]: defaultValue,
});
const createProviderBonusState = () =>
  createProviderModeState({
    isManuallyDeselected: false,
    rememberedBonusId: null,
    selectedBonus: null,
  });

const BonusesContext = createContext({
  amountToGetBonus: 0,
  bonusesList: [],
  clientBonuses: [],
  depositAmount: 0,
  freeSpinAsBonus: null,
  freeSpins: [],
  isLockedBonuses: false,
  isValidatingBonus: false,
  paymentMethod: null,
  selectedBonus: null,
  setDepositAmount: () => {},
  setPaymentMethod: () => {},
  setSelectedBonus: () => {},
});

const BonusesProvider = ({ children }) => {
  const bonusesService = useBonusesService();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [bonusStateByProviderType, setBonusStateByProviderType] =
    useState(createProviderBonusState);
  const [depositAmount, setDepositAmount] = useState(0);
  const [isLockedBonuses, setIsLockedBonuses] = useState(false);
  const [isValidatingBonus, setIsValidatingBonus] = useState(false);
  const validationRef = useRef({ counter: 0, isManualSelection: false, lastBonusKey: {} });
  const clientBonuses = bonusesService?.clientBonuses ?? EMPTY_BONUSES;
  const freeSpins = bonusesService?.freeSpins.freeSpins ?? EMPTY_BONUSES;
  const applicationState = useApplicationState();
  const { country, currency } = applicationState.account;
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);
  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const location = useLocation();
  const history = useHistory();
  const { pathname, state: locationState } = location;
  const previousShouldRefreshBonusesByRouteRef = useRef(false);
  const consumedPreselectKeyRef = useRef(null);

  const depositFreeSpin = locationState?.depositFreeSpin || null;

  const freeSpinAsBonus = useMemo(() => {
    if (!depositFreeSpin?.freeSpin) return null;

    const { bonusId, bonusInstanceId, freeSpin: freeSpinInstance } = depositFreeSpin;
    const { currency: freeSpinCurrency, expirationTime, freeSpin: freeSpinData } = freeSpinInstance;
    const {
      freeSpinMinAmountsDeposit,
      localizedImageInfoList = [],
      localizedInfoList = [],
    } = freeSpinData || {};
    const localizedInfo = localizedInfoList[0] || {};
    const minDeposit = freeSpinMinAmountsDeposit?.[currency] ?? 0;

    return {
      bonusId,
      bonusInstanceId,
      bonusType: 'FREESPIN',
      currency: freeSpinCurrency,
      disabled: false,
      endTime: expirationTime,
      expirationTime,
      financeData: { currency, minDeposit },
      financeDataList: [{ currency, minDeposit }],
      getImage: lang =>
        (
          localizedImageInfoList?.find(item => item.language === lang) ||
          localizedImageInfoList?.[0]
        )?.imageLink || '',
      id: `freespin-${bonusInstanceId}`,
      isFreeSpin: true,
      localizedImageInfoList,
      localizedInfoList,
      name: localizedInfo.name || 'Free Spin Bonus',
      notAllowedCountries: [],
      notAllowedPaymentMethods: [],
      productType: 'CASINO',
    };
  }, [currency, depositFreeSpin]);

  const depositBasePath = useMemo(
    () =>
      normalizePath(`${clientProfilePath}/${clientProfileRoutes.payment}/${paymentRoutes.deposit}`),
    [clientProfilePath],
  );

  const isDepositDetailsRoute = useMemo(() => {
    const normalizedPathname = normalizePath(pathname);
    const depositDetailsPattern = new RegExp(`^${escapeRegExp(depositBasePath)}/[^/]+$`);

    return depositDetailsPattern.test(normalizedPathname);
  }, [depositBasePath, pathname]);

  const shouldRefreshBonusesByRoute = useMemo(() => {
    return (
      pathname.includes(casinoPath) || pathname.includes(casinoLivePath) || isDepositDetailsRoute
    );
  }, [casinoLivePath, casinoPath, isDepositDetailsRoute, pathname]);

  const shouldUseQuickProviderMode = useMemo(() => {
    return pathname.includes(casinoPath) || pathname.includes(casinoLivePath);
  }, [casinoLivePath, casinoPath, pathname]);

  const providerType = useMemo(() => {
    return shouldUseQuickProviderMode ? PROVIDER_MODE.QUICK : PROVIDER_MODE.DEPOSIT;
  }, [shouldUseQuickProviderMode]);
  const { isManuallyDeselected, rememberedBonusId, selectedBonus } =
    bonusStateByProviderType[providerType];

  const setSelectedBonus = useCallback(
    nextSelectedBonus => {
      setBonusStateByProviderType(currentState => {
        const currentProviderState = currentState[providerType];
        const resolvedSelectedBonus =
          typeof nextSelectedBonus === 'function'
            ? nextSelectedBonus(currentProviderState.selectedBonus)
            : nextSelectedBonus;

        if (currentProviderState.selectedBonus === resolvedSelectedBonus) {
          return currentState;
        }

        return {
          ...currentState,
          [providerType]: {
            ...currentProviderState,
            selectedBonus: resolvedSelectedBonus,
          },
        };
      });
    },
    [providerType],
  );

  const setRememberedBonusId = useCallback(
    nextRememberedBonusId => {
      setBonusStateByProviderType(currentState => {
        const currentProviderState = currentState[providerType];

        if (currentProviderState.rememberedBonusId === nextRememberedBonusId) {
          return currentState;
        }

        return {
          ...currentState,
          [providerType]: {
            ...currentProviderState,
            rememberedBonusId: nextRememberedBonusId,
          },
        };
      });
    },
    [providerType],
  );

  const setIsManuallyDeselected = useCallback(
    isDeselected => {
      setBonusStateByProviderType(currentState => {
        const currentProviderState = currentState[providerType];

        if (currentProviderState.isManuallyDeselected === isDeselected) {
          return currentState;
        }

        return {
          ...currentState,
          [providerType]: {
            ...currentProviderState,
            isManuallyDeselected: isDeselected,
          },
        };
      });
    },
    [providerType],
  );

  const refreshBonuses = useCallback(async () => {
    if (!bonusesService) {
      return;
    }

    await bonusesService.load();
    await bonusesService.freeSpins.load(FREE_SPIN_LOAD_STATUSES);
  }, [bonusesService]);

  useEffect(() => {
    refreshBonuses();
  }, [refreshBonuses]);

  useEffect(() => {
    Bus.on(Bus.events.bonus.bonusInstanceStatusChanged, refreshBonuses);

    return () => Bus.off(Bus.events.bonus.bonusInstanceStatusChanged, refreshBonuses);
  }, [refreshBonuses]);

  useEffect(() => {
    if (!previousShouldRefreshBonusesByRouteRef.current && shouldRefreshBonusesByRoute) {
      refreshBonuses();
    }

    previousShouldRefreshBonusesByRouteRef.current = shouldRefreshBonusesByRoute;
  }, [refreshBonuses, shouldRefreshBonusesByRoute]);

  useEffect(() => {
    if (!paymentMethod?.id) {
      return;
    }

    setBonusStateByProviderType(currentState => ({
      [PROVIDER_MODE.DEPOSIT]: {
        ...currentState[PROVIDER_MODE.DEPOSIT],
        isManuallyDeselected: false,
      },
      [PROVIDER_MODE.QUICK]: {
        ...currentState[PROVIDER_MODE.QUICK],
        isManuallyDeselected: false,
      },
    }));
  }, [paymentMethod?.id]);

  const isBonusAllowed = useCallback(
    bonus =>
      !getBonusNotAllowedPaymentMethods(bonus).map(String).includes(String(paymentMethod?.id)) &&
      !getBonusNotAllowedCountries(bonus).includes(country?.toLowerCase()),
    [country, paymentMethod?.id],
  );
  const isSelectableBonus = useCallback(
    bonus => {
      if (!bonus || bonus.disabled || !isBonusAllowed(bonus)) {
        return false;
      }

      return !isAmountBelowMinDeposit(depositAmount, getMinDeposit(bonus, currency));
    },
    [currency, depositAmount, isBonusAllowed],
  );

  const makeDepositBonuses = useCallback(() => {
    return [...clientBonuses, ...freeSpins]
      .filter(
        bonus =>
          (isDepositBonus(bonus?.type, [DEPOSIT, RELOAD_DEPOSIT]) ||
            isDepositFreeSpin(bonus?.subType, [DEPOSIT, RELOAD_DEPOSIT])) &&
          bonus.status === ACTIVATION_READY,
      )
      .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
      .sort(sortBonusesByAvailabilityAndAmount);
  }, [clientBonuses, freeSpins, isBonusAllowed]);

  const casinoBonuses = useMemo(
    () =>
      makeDepositBonuses().filter(bonus => {
        return (
          isCasinoBonus(bonus.productType, CASINO_PRODUCT_TYPE) ||
          isDepositFreeSpin(bonus.subType, [DEPOSIT, RELOAD_DEPOSIT])
        );
      }),
    [makeDepositBonuses],
  );

  const quickDepositBonuses = useMemo(
    () =>
      casinoBonuses
        .map(bonus => markBonusAvailability(bonus, isBonusAllowed))
        .sort(sortBonusesByAvailabilityAndAmount),
    [casinoBonuses, isBonusAllowed],
  );

  const bonusesList = useMemo(() => {
    if (providerType === PROVIDER_MODE.QUICK) {
      return quickDepositBonuses;
    }

    return makeDepositBonuses();
  }, [makeDepositBonuses, providerType, quickDepositBonuses]);

  const rememberedBonus = useMemo(() => {
    const bonusesSource = providerType === PROVIDER_MODE.QUICK ? quickDepositBonuses : bonusesList;

    return bonusesSource.find(bonus => bonus.id === rememberedBonusId) ?? null;
  }, [bonusesList, providerType, quickDepositBonuses, rememberedBonusId]);

  const amountToGetBonus = useMemo(() => {
    const amount = Number(depositAmount);
    if (!depositAmount || Number.isNaN(amount)) return 0;

    const bonus = selectedBonus || rememberedBonus;
    if (!bonus || bonus.disabled || !isBonusAllowed(bonus)) return 0;

    const minDeposit = getMinDeposit(bonus, currency);

    return Math.max(minDeposit - amount, 0);
  }, [currency, depositAmount, isBonusAllowed, rememberedBonus, selectedBonus]);

  useEffect(() => {
    if (providerType === PROVIDER_MODE.QUICK) {
      setIsLockedBonuses(false);

      const currentSelectedBonus =
        quickDepositBonuses.find(bonus => bonus.id === selectedBonus?.id) ?? null;
      const syncedBonuses = [currentSelectedBonus, rememberedBonus]
        .filter(Boolean)
        .map(({ id }) =>
          quickDepositBonuses.find(bonus => (bonus.bonusInstanceId ?? bonus.id) === id),
        )
        .filter(Boolean);
      const selectableBonus = syncedBonuses.find(isSelectableBonus);
      const hasBonusBelowMinDeposit = syncedBonuses.some(bonus =>
        isAmountBelowMinDeposit(depositAmount, getMinDeposit(bonus, currency)),
      );
      const firstAvailableBonus =
        quickDepositBonuses.find(
          bonus =>
            !isDepositFreeSpin(bonus?.subType, [DEPOSIT, RELOAD_DEPOSIT]) &&
            isSelectableBonus(bonus),
        ) ?? null;
      const nextBonus =
        selectableBonus ||
        (!hasBonusBelowMinDeposit && !isManuallyDeselected ? firstAvailableBonus : null);

      if (!nextBonus) {
        setSelectedBonus(null);
        return;
      }

      if (rememberedBonusId !== nextBonus.id) {
        setRememberedBonusId(nextBonus.id);
      }

      if (selectedBonus?.id !== nextBonus.id || selectedBonus !== nextBonus) {
        setSelectedBonus(nextBonus);
      }
    }
  }, [
    bonusesList,
    currency,
    depositAmount,
    freeSpinAsBonus,
    isSelectableBonus,
    isManuallyDeselected,
    providerType,
    quickDepositBonuses,
    rememberedBonus,
    rememberedBonusId,
    selectedBonus,
    setRememberedBonusId,
    setSelectedBonus,
  ]);

  useEffect(() => {
    if (providerType !== PROVIDER_MODE.DEPOSIT || !selectedBonus) {
      return;
    }

    setRememberedBonusId(selectedBonus.id);
  }, [providerType, selectedBonus, setRememberedBonusId]);

  useEffect(() => {
    if (providerType !== PROVIDER_MODE.DEPOSIT || freeSpinAsBonus) {
      return;
    }

    setIsLockedBonuses(false);
    setSelectedBonus(currentSelectedBonus => {
      const syncedBonuses = [currentSelectedBonus, rememberedBonus]
        .filter(Boolean)
        .map(({ id }) => findBonusById(bonusesList, id))
        .filter(Boolean);
      const selectableBonus = syncedBonuses.find(isSelectableBonus);
      const hasBonusBelowMinDeposit = syncedBonuses.some(bonus =>
        isAmountBelowMinDeposit(depositAmount, getMinDeposit(bonus, currency)),
      );

      if (selectableBonus) {
        return selectableBonus;
      }

      if (hasBonusBelowMinDeposit || isManuallyDeselected) {
        return null;
      }

      return (
        bonusesList.find(
          bonus =>
            !isDepositFreeSpin(bonus?.subType, [DEPOSIT, RELOAD_DEPOSIT]) &&
            isSelectableBonus(bonus),
        ) ?? null
      );
    });
  }, [
    bonusesList,
    currency,
    depositAmount,
    freeSpinAsBonus,
    isSelectableBonus,
    isManuallyDeselected,
    providerType,
    rememberedBonus,
    setSelectedBonus,
  ]);

  const handleSetSelectedBonus = useCallback(
    bonus => {
      validationRef.current.lastBonusKey[providerType] = null;
      if (!bonus) {
        setIsManuallyDeselected(true);
        setRememberedBonusId(null);
        setSelectedBonus(null);
        return;
      }

      validationRef.current.isManualSelection = true;
      setIsManuallyDeselected(false);
      setRememberedBonusId(bonus.id);
      setSelectedBonus(bonus);
    },
    [providerType, setIsManuallyDeselected, setRememberedBonusId, setSelectedBonus],
  );

  useEffect(() => {
    validationRef.current.lastBonusKey[providerType] = null;
    setBonusStateByProviderType(currentState => ({
      ...currentState,
      [providerType]: {
        ...currentState[providerType],
        isManuallyDeselected: false,
      },
    }));
  }, [providerType]);

  useEffect(() => {
    if (!shouldUseQuickProviderMode && !isDepositDetailsRoute) {
      validationRef.current.counter = 0;
      setIsValidatingBonus(false);
      return;
    }

    if (!selectedBonus?.id && !selectedBonus?.bonusInstanceId) {
      validationRef.current.counter = 0;
      setIsValidatingBonus(false);
      return;
    }

    const bonusKey = selectedBonus?.bonusInstanceId ?? selectedBonus?.id;

    if (bonusKey === validationRef.current.lastBonusKey[providerType]) {
      setSelectedBonus(null);
      setRememberedBonusId(null);
      setIsManuallyDeselected(true);
      setIsValidatingBonus(false);
      return;
    }

    const requestId = ++validationRef.current.counter;
    const isManualSelection = validationRef.current.isManualSelection;
    validationRef.current.isManualSelection = false;

    setIsValidatingBonus(true);

    BonusApi.validateBonusActivation(bonusKey)
      .then(result => {
        if (validationRef.current.counter !== requestId) return;

        if (!result?.valid) {
          validationRef.current.lastBonusKey[providerType] = bonusKey;
          setSelectedBonus(null);
          setRememberedBonusId(null);
          setIsManuallyDeselected(true);
          if (isManualSelection) {
            NotificationManager.toast({
              id: 'bonusValidationError',
              status: 'error',
              title: 'activation_bonus_error',
              type: 'toast',
            });
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (validationRef.current.counter === requestId) {
          setIsValidatingBonus(false);
        }
      });
  }, [
    selectedBonus?.id,
    selectedBonus?.bonusInstanceId,
    setIsManuallyDeselected,
    setRememberedBonusId,
    setSelectedBonus,
    shouldUseQuickProviderMode,
    isDepositDetailsRoute,
    providerType,
  ]);

  useEffect(() => {
    if (providerType !== PROVIDER_MODE.DEPOSIT) {
      return;
    }

    const { depositFreeSpin: depositFreeSpinTarget, preselectBonus } = location.state ?? {};

    const preselectTarget =
      preselectBonus ??
      (depositFreeSpinTarget
        ? {
            bonusId: depositFreeSpinTarget.bonusId ?? null,
            bonusInstanceId: depositFreeSpinTarget.bonusInstanceId ?? null,
          }
        : null);

    if (!preselectTarget) {
      return;
    }

    const preselectKey = `${preselectTarget.bonusId ?? ''}:${preselectTarget.bonusInstanceId ?? ''}`;
    if (consumedPreselectKeyRef.current === preselectKey) {
      return;
    }

    if (bonusesList.length === 0) {
      return;
    }

    const match = findPreselectBonus(bonusesList, preselectTarget);
    if (!match) {
      return;
    }

    consumedPreselectKeyRef.current = preselectKey;
    handleSetSelectedBonus(match);

    const remainingEntries = Object.entries(location.state).filter(
      ([key]) => key !== 'preselectBonus' && key !== 'depositFreeSpin',
    );
    history.replace({
      hash: location.hash,
      pathname: location.pathname,
      search: location.search,
      state: remainingEntries.length > 0 ? Object.fromEntries(remainingEntries) : undefined,
    });
  }, [bonusesList, handleSetSelectedBonus, history, location, providerType]);

  const value = useMemo(
    () => ({
      amountToGetBonus,
      bonusesList,
      clientBonuses,
      depositAmount,
      freeSpinAsBonus,
      freeSpins,
      isLockedBonuses,
      isValidatingBonus,
      paymentMethod,
      selectedBonus,
      setDepositAmount,
      setPaymentMethod,
      setSelectedBonus: handleSetSelectedBonus,
    }),
    [
      depositAmount,
      clientBonuses,
      freeSpins,
      amountToGetBonus,
      bonusesList,
      freeSpinAsBonus,
      handleSetSelectedBonus,
      isLockedBonuses,
      isValidatingBonus,
      paymentMethod,
      selectedBonus,
    ],
  );
  return <BonusesContext.Provider value={value}>{children}</BonusesContext.Provider>;
};

BonusesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useBonusesContext = () => useContext(BonusesContext);
export default BonusesProvider;
